import { memo, type CSSProperties } from 'react';
import { motion } from 'motion/react';
import clsx from 'clsx';
import { PREMIUM_LABEL, cellName } from '@wordloom/engine';
import type { PremiumKind } from '@wordloom/engine';
import { IconArrowDown, IconArrowRight } from './Icons';
import { TileFace } from './Tile';
import './Cell.css';

/** Spring used whenever a tile lands on the board (UX-SPEC §6). */
export const PLACE_SPRING = { type: 'spring', stiffness: 520, damping: 32 } as const;

export type CellContent = 'empty' | 'committed' | 'draft';

/**
 * Every prop is a primitive so the default shallow `memo` comparison skips
 * all 225 squares except the few that actually changed.
 */
export interface CellProps {
  row: number;
  col: number;
  premium: PremiumKind | null;
  center: boolean;
  content: CellContent;
  tileId: string | null;
  letter: string;
  value: number;
  blank: boolean;
  /** Committed tiles: who played it (for the accessible label). */
  playedBy: string | null;
  /** Roving tabindex target. */
  tabbable: boolean;
  /** Keyboard cursor is here (drawn while the board has keyboard focus). */
  cursor: boolean;
  /** Typing direction at the cursor; null once the game is over (nothing to type). */
  direction: 'H' | 'V' | null;
  hint: boolean;
  bad: boolean;
  last: boolean;
  /** Opponent tile that just landed: drop-in order (−1 = none). */
  dropIndex: number;
  /** My tile that was just committed: settle animation. */
  settle: boolean;
  /** Draft tile currently being dragged (hidden in place). */
  dragging: boolean;
  /** Draft tile should glide with a shared layout animation (not after a drag drop). */
  shared: boolean;
  pending: boolean;
  shakeNonce: number;
  starPulse: boolean;
}

/** The square's contents; then whether it is part of the invalid move, and the typing direction at the cursor. */
function describe(p: CellProps): string {
  const name = cellName(p.row, p.col);
  let text: string;
  if (p.content === 'committed') {
    const blank = p.blank ? ', blank' : '';
    const pts = p.blank ? '0 points' : `${p.value} ${p.value === 1 ? 'point' : 'points'}`;
    text = `${name}, letter ${p.letter}${blank}, ${pts}${p.playedBy ? `, placed by ${p.playedBy}` : ''}`;
  } else if (p.content === 'draft') {
    const tile = !p.blank ? p.letter : p.letter ? `blank as ${p.letter}, 0 points` : 'blank, no letter yet';
    text = `${name}, your tile ${tile}, not submitted`;
  } else {
    const parts = [name];
    if (p.premium) parts.push(PREMIUM_LABEL[p.premium].long.toLowerCase());
    if (p.center) parts.push('star');
    parts.push('empty');
    text = parts.join(', ');
  }
  // Generic on purpose: the preview / toast says what is wrong with the move.
  if (p.bad) text += ', part of an invalid move';
  if (p.cursor && p.direction) text += p.direction === 'H' ? ', typing across' : ', typing down';
  return text;
}

function CellImpl(p: CellProps) {
  const label = describe(p);
  const hasTile = p.content !== 'empty';
  const tileStyle: CSSProperties | undefined =
    p.dropIndex >= 0 ? ({ '--drop-delay': `${p.dropIndex * 70}ms` } as CSSProperties) : undefined;

  return (
    <div
      role="gridcell"
      className={clsx('cell', p.premium && `cell--${p.premium.toLowerCase()}`, p.center && 'cell--center')}
      data-cell={`${p.row},${p.col}`}
      data-hint={p.hint && !hasTile ? '' : undefined}
      data-cursor={p.cursor ? (p.direction ?? '') : undefined}
      data-star-pulse={p.starPulse ? '' : undefined}
      tabIndex={p.tabbable ? 0 : -1}
      aria-label={label}
    >
      {!hasTile && p.premium && (
        <span className="cell__premium" aria-hidden="true">
          {p.center ? '★' : PREMIUM_LABEL[p.premium].short}
        </span>
      )}
      {p.content === 'committed' && (
        <div
          className={clsx(
            'tile',
            'cell__tile',
            p.last && 'is-last',
            p.bad && 'is-bad',
            p.dropIndex >= 0 && 'is-dropping',
            p.settle && 'is-settling',
          )}
          data-blank={p.blank || undefined}
          style={tileStyle}
        >
          <TileFace letter={p.letter} value={p.value} blank={p.blank} />
        </div>
      )}
      {p.content === 'draft' && p.tileId && (
        <motion.div
          key={p.shakeNonce}
          layoutId={p.shared ? `tile-${p.tileId}` : undefined}
          transition={{ layout: PLACE_SPRING }}
          className={clsx(
            'tile',
            'cell__tile',
            'is-draft',
            p.bad && 'is-bad',
            p.bad && p.shakeNonce > 0 && 'is-shaking',
            p.pending && 'is-pending',
            p.dragging && 'is-dragging',
          )}
          data-draft-tile=""
          data-tile-id={p.tileId}
          data-blank={p.blank || undefined}
        >
          <TileFace letter={p.letter} value={p.value} blank={p.blank} />
        </motion.div>
      )}
      {p.cursor && p.direction && (
        <span className="cell__dir" aria-hidden="true">
          {p.direction === 'H' ? <IconArrowRight /> : <IconArrowDown />}
        </span>
      )}
    </div>
  );
}

export const Cell = memo(CellImpl);
